/**
 * @documental
 * @export InputGroup
 */

import { FC, useMemo, PropsWithChildren } from "react";
import { DisableGroup } from "capitalrx-components-2.0/lib/general";
import { InputGroupContext, InputGroupContextValue } from "./InputGroupContext";
import { useInputReadonly } from "./useInputReadonly";

/**
 * The `InputGroup` component properties.
 */
export interface InputGroupProps {
  /**
   * Indicates whether descendent inputs should be disabled.
   */
  readonly disabled?: boolean;

  /**
   * Specifies whether descendent inputs should display as read-only.
   */
  readonly readOnly?: boolean;
}

/**
 * Provides methods for the management of behavior for a group of inputs.
 *
 * `import { Radio } from "capitalrx-components-2.0/lib/forms";`
 *
 * <strong>Example use case:</strong>
 *
 * ```typescript
  <Form>
    <InputGroup {...props}>
      <RadioGroup.Field name="textbox" defaultValue="radio1" label="Radio Group" {...props}>
        <RadioGroup.Option value="radio1">Radio 1</RadioGroup.Option>
        <RadioGroup.Option value="radio2">Radio 2</RadioGroup.Option>
        <RadioGroup.Option value="radio3">Radio 3</RadioGroup.Option>
      </RadioGroup.Field>
    </InputGroup>
  </Form>
  ```
 */

export const InputGroup: FC<PropsWithChildren<InputGroupProps>> = (props) => {
  const { children, disabled } = props;

  const readOnly = useInputReadonly(props);

  const contextValue = useMemo(
    (): InputGroupContextValue => ({
      readOnly,
    }),
    [readOnly],
  );

  return (
    <DisableGroup disabled={disabled}>
      <InputGroupContext.Provider value={contextValue}>{children}</InputGroupContext.Provider>
    </DisableGroup>
  );
};
